import { graphql } from 'gatsby';
import React from 'react';

import favicon from '../assets/images/CK-Favicon.png';
import BlockPost from '../components/BlockPost';
import BlockRecommended from '../components/BlockRecommended';
import BlockContactSlice from '../components/slices/BlockContactSlice';
import { Schemas } from '../seo/Schemas';
import { getMetaDesc, getMetaTitle } from '../utils/blogData';

export const Head = ({ data, pageContext, location: { pathname } }) => {
  const { post } = data;

  if (!post) return null;

  const metaDesc = getMetaDesc(post);
  const metaTitle = getMetaTitle(post);

  // TODO: This should use the Meta component
  return (
    <>
      <html dir="auto" lang="en-us" />
      <title>{metaTitle}</title>
      <meta name="description" content={metaDesc} />
      <link rel="canonical" href={`https://cloudkitchens.com${pathname}`} />
      <link rel="icon" href={favicon} />
      <link
        rel="alternate"
        hrefLang="en-us"
        href={`https://cloudkitchens.com${pathname}`}
      />

      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta
        property="og:url"
        content={`https://cloudkitchens.com${pathname}`}
      />
      <meta property="og:site_name" content="CloudKitchens" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:title" content={metaTitle} />

      <meta
        name="robots"
        content="follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large"
      />

      {post?.featuredImage?.node && (
        <>
          <meta
            property="og:image"
            content={post.featuredImage.node.sourceUrl}
          />
          <meta
            property="og:image:secure_url"
            content={post.featuredImage.node.sourceUrl}
          />
          <meta
            property="og:image:width"
            content={post.featuredImage.node.width}
          />
          <meta
            property="og:image:height"
            content={post.featuredImage.node.height}
          />
          <meta
            name="twitter:image"
            content={post.featuredImage.node.sourceUrl}
          />
        </>
      )}

      <Schemas data={data} pageContext={pageContext} />
    </>
  );
};

const Post = ({
  data: { previous, post, next, blogPagePartial, homePagePartial },
  pageContext: { siteURL },
}) => {
  const homePageContactSlice = homePagePartial.data.body.find(
    (slice) => slice.slice_type === 'block_contact'
  );
  const blogPageBlogBannerSlice = blogPagePartial.data.body.find(
    (slice) => slice.slice_type === 'block_blog_banner'
  );
  return (
    <>
      <BlockPost
        post={post}
        siteURL={siteURL}
        banner={blogPageBlogBannerSlice}
      />
      <BlockRecommended next={next} previous={previous} />
      {homePageContactSlice && (
        <BlockContactSlice slice={homePageContactSlice} />
      )}
    </>
  );
};

export default Post;

export const query = graphql`
  query BlogPostById(
    $id: String!
    $previousPostId: String
    $nextPostId: String
    $siteTag: String!
  ) {
    post: wpPost(id: { eq: $id }) {
      id
      slug
      title
      excerpt
      content
      date
      modified
      author {
        node {
          id
          name
          slug
        }
      }
      featuredImage {
        node {
          altText
          sourceUrl
          width
          height
          localFile {
            childImageSharp {
              fluid(maxWidth: 1000, quality: 100) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
      }
      seo {
        metaDesc
        title
      }
    }

    # this gets us the previous post by id (if it exists)
    previous: wpPost(id: { eq: $previousPostId }) {
      uri
      slug
      title
      excerpt
      content
      date(formatString: "MMMM DD, YYYY")
      featuredImage {
        node {
          altText
          localFile {
            childImageSharp {
              fluid(maxWidth: 1000, quality: 100) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
      }
    }

    # this gets us the next post by id (if it exists)
    next: wpPost(id: { eq: $nextPostId }) {
      uri
      slug
      title
      excerpt
      content
      date(formatString: "MMMM DD, YYYY")
      featuredImage {
        node {
          altText
          localFile {
            childImageSharp {
              fluid(maxWidth: 1000, quality: 100) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
      }
    }

    blogPagePartial: prismicPage(
      data: { slug: { eq: "/blog/" } }
      tags: { eq: $siteTag }
    ) {
      id
      _previewable
      data {
        body {
          ... on PrismicPageDataBodyBlockBlogBanner {
            id
            primary {
              eyebrow {
                richText
                text
              }
              content {
                html
                richText
                text
              }
              typeform_id
              image1 {
                url
                alt
              }
            }
            slice_label
            slice_type
          }
        }
      }
    }

    homePagePartial: prismicPage(
      data: { slug: { eq: "/" } }
      tags: { eq: $siteTag }
    ) {
      id
      _previewable
      data {
        body {
          ... on PrismicPageDataBodyBlockContact {
            id
            primary {
              background_image {
                url
                alt
              }
              button {
                html
                richText
                text
              }
              color
              content {
                html
                richText
                text
              }
              title_end {
                html
                richText
                text
              }
              title_middle {
                html
                richText
                text
              }
              title_start {
                html
                richText
                text
              }
            }
            slice_label
            slice_type
          }
        }
      }
    }
  }
`;
